import { useState } from "react";
import { useSessao } from "@/sessao/useSessao";
import { useConvenios, useCriarVinculo, usePessoas } from "@/api/consultas";
import { FalhaDaApi, textoDoErro } from "@/api/problema";
import { Selecao } from "@/componentes/Selecao";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { useAvisos } from "@/componentes/Avisos";
import { documento } from "@/formato";
import { EscolhaDeConvenios } from "./EscolhaDeConvenios";

/*
 * Vincula um beneficiário à aplicação.
 *
 * Com plano padrão (aplicação `por_cedente`), o servidor abre o contrato do
 * par na MESMA transação: a tela não faz um segundo pedido, e não há como o
 * vínculo nascer pela metade. Sem plano padrão, o cedente aparece na lista
 * como "não emite", com a ação de contrato ao lado.
 */
export function NovoVinculo({
  sistemaID,
  sistemaNome,
  jaVinculados,
  aoFechar,
}: {
  sistemaID: string;
  sistemaNome: string;
  /** Fora da lista: vincular de novo é `409 vinculo_existente`. */
  jaVinculados: ReadonlySet<string>;
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const pessoas = usePessoas(cliente, true);
  const convenios = useConvenios(cliente, true);
  const criar = useCriarVinculo(cliente, sistemaID);

  const [pessoaID, setPessoaID] = useState("");
  const [permitidos, setPermitidos] = useState<string[] | null>(null);
  const [erroDaPessoa, setErroDaPessoa] = useState<string | undefined>(undefined);
  const [erroGeral, setErroGeral] = useState<string | undefined>(undefined);

  const opcoes: (readonly [string, string])[] = [
    ["", "—"],
    ...(pessoas.data?.pessoas ?? [])
      .filter((pessoa) => !jaVinculados.has(pessoa.id))
      .map(
        (pessoa) => [pessoa.id, `${pessoa.razao_social} · ${documento(pessoa.documento)}`] as const,
      ),
  ];
  const doBeneficiario = (convenios.data?.convenios ?? []).filter(
    (convenio) => convenio.pessoa_id === pessoaID,
  );

  function enviar() {
    setErroGeral(undefined);
    if (pessoaID === "") {
      setErroDaPessoa("Escolha o cedente.");
      return;
    }
    setErroDaPessoa(undefined);

    criar.mutate(
      // A lista vai SEMPRE, inclusive nula: ausente e nulo dizem o mesmo hoje
      // na criação, mas um corpo explícito não depende disso continuar assim.
      { pessoa_id: pessoaID, convenios_permitidos: permitidos },
      {
        onSuccess: () => {
          avisos.feito("Vínculo criado");
          aoFechar();
        },
        onError: (falha) => {
          if (falha instanceof FalhaDaApi && falha.problema?.campo === "pessoa_id") {
            setErroDaPessoa(textoDoErro(falha));
          } else {
            setErroGeral(textoDoErro(falha));
          }
        },
      },
    );
  }

  return (
    <ModalDeCadastro
      aberto
      aoFechar={aoFechar}
      titulo="Vincular cedente"
      descricao={sistemaNome}
      aoEnviar={enviar}
      enviando={criar.isPending}
      erro={erroGeral}
      rotuloDeEnvio="Vincular"
    >
      <Selecao
        rotulo="Cedente"
        opcoes={opcoes}
        value={pessoaID}
        onChange={(evento) => {
          setPessoaID(evento.target.value);
          // Os convênios marcados eram do beneficiário anterior: a API
          // recusaria todos com `convenio_de_outro_beneficiario`.
          setPermitidos((atual) => (atual === null ? null : []));
        }}
        erro={erroDaPessoa}
        autoFocus
      />

      {pessoaID !== "" && (
        <EscolhaDeConvenios
          convenios={doBeneficiario}
          valor={permitidos}
          aoMudar={setPermitidos}
        />
      )}
    </ModalDeCadastro>
  );
}
